// src\components\admin\ProjectPreviewModal.jsx
import React, { useEffect, useState } from 'react';
import {
  FiX,
  FiCalendar,
  FiTag,
  FiUser,
  FiStar,
  FiExternalLink,
  FiHeart,
  FiEye,
  FiTrendingUp,
  FiShare2,
  FiCopy,
  FiCheck,
  FiImage,
  FiChevronLeft,
  FiChevronRight,
  FiMaximize2,
  FiMousePointer,
} from 'react-icons/fi';
import { useTheme } from '../../contexts/ThemeContext';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Zoom } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';

function ProjectPreviewModal({ isOpen, onClose, project }) {
  const { actualTheme } = useTheme();
  const [copied, setCopied] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageErrors, setImageErrors] = useState({});
  const [imagesLoaded, setImagesLoaded] = useState({});

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleKeyPress = e => {
    if (e.key === 'Escape') onClose();
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const handleCopyLink = async () => {
    const url = `${window.location.origin}/portfolio/${project.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSlideChange = swiper => {
    setCurrentImageIndex(swiper.activeIndex);
  };

  const handleImageError = index => {
    setImageErrors(prev => ({ ...prev, [index]: true }));
  };

  const handleImageLoad = index => {
    setImagesLoaded(prev => ({ ...prev, [index]: true }));
  };

  if (!isOpen || !project) return null;

  const getOptimizedImageUrl = (url, width = 1200) => {
    if (!url) return '';
    if (url.includes('cloudinary.com'))
      return url.replace('/upload/', `/upload/w_${width},q_auto,f_auto/`);
    return url;
  };

  const formatDate = dateString => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getImageAspectRatio = image => {
    if (image.ratio) return image.ratio;
    if (image.width && image.height) return image.width / image.height;
    return 1.33;
  };

  const images =
    project.images || (project.imageUrl ? [{ url: project.imageUrl }] : []);
  const imageCount = images.length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md transition-opacity duration-300" />

      {/* Modal */}
      <div
        className="relative max-w-5xl w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl transition-all duration-300 animate-fade-in-up"
        style={{ backgroundColor: 'var(--bg)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 p-2 rounded-full transition-all hover:scale-110"
          style={{
            backgroundColor: 'var(--bg)',
            color: 'var(--text)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          <FiX className="w-5 h-5" />
        </button>

        {/* Image Slider Section - Facebook/Messenger style */}
        <div
          className="relative w-full bg-black overflow-hidden"
          style={{
            height: '55vh',
            minHeight: '300px',
            maxHeight: '600px',
          }}
        >
          {images.length === 0 ? (
            <div className="w-full h-full flex flex-col items-center justify-center p-8 text-center">
              <FiImage
                className="w-12 h-12 mb-3 opacity-30"
                style={{ color: 'var(--text)' }}
              />
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                No images available
              </p>
            </div>
          ) : (
            <>
              <Swiper
                modules={[Navigation, Pagination, Zoom]}
                navigation={{
                  nextEl: '.swiper-button-next',
                  prevEl: '.swiper-button-prev',
                }}
                pagination={{
                  clickable: true,
                  dynamicBullets: true,
                }}
                zoom={{
                  maxRatio: 3,
                  minRatio: 1,
                }}
                loop={imageCount > 1}
                onSlideChange={handleSlideChange}
                className="preview-modal-swiper"
                style={{
                  width: '100%',
                  height: '100%',
                }}
              >
                {images.map((image, index) => {
                  const imageUrl = image.url || image;
                  const isError = imageErrors[index];
                  const isLoaded = imagesLoaded[index];
                  const ratio = getImageAspectRatio(image);
                  const isPortrait = ratio < 0.9;

                  return (
                    <SwiperSlide key={index}>
                      <div
                        className="w-full h-full flex items-center justify-center bg-black"
                        style={{ position: 'relative' }}
                      >
                        {!isLoaded && !isError && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div
                              className="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin"
                              style={{ borderColor: 'var(--text)' }}
                            />
                          </div>
                        )}
                        {isError ? (
                          <div className="w-full h-full flex flex-col items-center justify-center p-8 text-center">
                            <FiImage
                              className="w-12 h-12 mb-3 opacity-30"
                              style={{ color: 'var(--text)' }}
                            />
                            <p
                              className="text-sm"
                              style={{ color: 'var(--muted)' }}
                            >
                              Unable to load image
                            </p>
                          </div>
                        ) : (
                          <img
                            src={getOptimizedImageUrl(imageUrl, 1400)}
                            alt={`${project.title} - Image ${index + 1}`}
                            className={`transition-opacity duration-500 ${
                              isLoaded ? 'opacity-100' : 'opacity-0'
                            }`}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              width: isPortrait ? 'auto' : '100%',
                              height: isPortrait ? '100%' : 'auto',
                              objectFit: 'contain',
                              display: 'block',
                            }}
                            onLoad={() => handleImageLoad(index)}
                            onError={() => handleImageError(index)}
                            loading="lazy"
                          />
                        )}
                      </div>
                    </SwiperSlide>
                  );
                })}
              </Swiper>

              {/* Navigation Buttons */}
              {imageCount > 1 && (
                <>
                  <button
                    className="swiper-button-prev absolute left-3 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full backdrop-blur-md transition-all hover:scale-110 hover:bg-black/70"
                    style={{
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                    }}
                  >
                    <FiChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    className="swiper-button-next absolute right-3 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full backdrop-blur-md transition-all hover:scale-110 hover:bg-black/70"
                    style={{
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                    }}
                  >
                    <FiChevronRight className="w-5 h-5" />
                  </button>

                  {/* Image Counter */}
                  <div
                    className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full backdrop-blur-md text-xs font-medium z-10"
                    style={{
                      backgroundColor: 'rgba(0,0,0,0.7)',
                      color: 'white',
                    }}
                  >
                    {currentImageIndex + 1} / {imageCount}
                  </div>
                </>
              )}

              {/* Featured Badge */}
              {project.featured && (
                <div className="absolute top-3 left-3 px-3 py-1.5 rounded-full text-xs font-medium bg-yellow-500 text-white flex items-center gap-1.5 shadow-lg z-10">
                  <FiStar className="w-3 h-3" />
                  Featured
                </div>
              )}

              {/* Type Badge */}
              <div className="absolute top-3 left-3 mt-9 px-3 py-1.5 rounded-full text-xs font-medium bg-black/60 text-white flex items-center gap-1.5 backdrop-blur-sm z-10">
                <span>{project.type === 'random' ? '🎲' : '📁'}</span>
                <span>{project.type || 'project'}</span>
              </div>

              {/* Stats Badges - All metrics */}
              <div className="absolute bottom-3 right-3 flex flex-wrap gap-1.5 z-10 justify-end max-w-[70%]">
                {project.likes > 0 && (
                  <div className="px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1 bg-black/70 text-white backdrop-blur-sm">
                    <FiHeart className="w-3 h-3 text-red-400" />
                    <span>{project.likes.toLocaleString()}</span>
                  </div>
                )}
                {project.views > 0 && (
                  <div className="px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1 bg-black/70 text-white backdrop-blur-sm">
                    <FiEye className="w-3 h-3 text-blue-400" />
                    <span>{project.views.toLocaleString()}</span>
                  </div>
                )}
                {project.clicks > 0 && (
                  <div className="px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1 bg-black/70 text-white backdrop-blur-sm">
                    <FiMousePointer className="w-3 h-3 text-green-400" />
                    <span>{project.clicks.toLocaleString()}</span>
                  </div>
                )}
                {project.shares > 0 && (
                  <div className="px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1 bg-black/70 text-white backdrop-blur-sm">
                    <FiShare2 className="w-3 h-3 text-purple-400" />
                    <span>{project.shares.toLocaleString()}</span>
                  </div>
                )}
              </div>

              {/* Zoom Indicator */}
              {imageCount > 0 && (
                <div className="absolute bottom-3 right-3 mt-14 px-2 py-1 rounded-md text-xs font-medium bg-black/40 text-white/60 backdrop-blur-sm z-10 flex items-center gap-1">
                  <FiMaximize2 className="w-3 h-3" />
                  <span>Click to zoom</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Content Section */}
        <div className="p-6 md:p-8">
          {/* Title and Actions */}
          <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
            <div className="flex-1">
              <h2
                className="text-2xl md:text-3xl font-bold mb-2"
                style={{ color: 'var(--text)' }}
              >
                {project.title}
              </h2>
              <div
                className="flex flex-wrap gap-4 text-sm"
                style={{ color: 'var(--muted)' }}
              >
                <span className="flex items-center gap-1.5">
                  <FiCalendar className="w-4 h-4" />
                  {formatDate(project.createdAt)}
                </span>
                <span className="flex items-center gap-1.5">
                  <FiTag className="w-4 h-4" />
                  <span className="capitalize">{project.category}</span>
                </span>
                {project.client && project.type !== 'random' && (
                  <span className="flex items-center gap-1.5">
                    <FiUser className="w-4 h-4" />
                    {project.client}
                  </span>
                )}
                <span className="flex items-center gap-1.5">
                  <FiImage className="w-4 h-4" />
                  {imageCount} {imageCount === 1 ? 'image' : 'images'}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCopyLink}
                className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all hover:opacity-80"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--text)',
                }}
                title="Copy link"
              >
                {copied ? (
                  <FiCheck className="w-4 h-4 text-green-500" />
                ) : (
                  <FiCopy className="w-4 h-4" />
                )}
                <span className="text-sm hidden sm:inline">
                  {copied ? 'Copied!' : 'Copy Link'}
                </span>
              </button>
              <a
                href={`/portfolio/${project.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all hover:gap-3"
                style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
              >
                <span className="text-sm">View Live</span>
                <FiExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Description */}
          <div className="mb-6">
            <h3
              className="text-lg font-semibold mb-3 flex items-center gap-2"
              style={{ color: 'var(--text)' }}
            >
              <span className="w-1 h-6 rounded-full bg-blue-500" />
              Project Description
            </h3>
            <p
              className="leading-relaxed whitespace-pre-wrap"
              style={{ color: 'var(--muted)' }}
            >
              {project.description}
            </p>
          </div>

          {/* Tags */}
          {project.tags && project.tags.length > 0 && (
            <div className="mb-6">
              <h3
                className="text-lg font-semibold mb-3 flex items-center gap-2"
                style={{ color: 'var(--text)' }}
              >
                <span className="w-1 h-6 rounded-full bg-purple-500" />
                Technologies & Tools
              </h3>
              <div className="flex flex-wrap gap-2">
                {project.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1.5 rounded-full text-sm transition-all hover:opacity-80"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Engagement Stats - All metrics with icons */}
          {(project.likes > 0 ||
            project.views > 0 ||
            project.clicks > 0 ||
            project.shares > 0) && (
            <div
              className="pt-6 mt-6 border-t flex flex-wrap gap-6"
              style={{ borderColor: 'var(--border)' }}
            >
              {project.likes > 0 && (
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/20">
                    <FiHeart className="w-4 h-4 text-red-500" />
                  </div>
                  <div>
                    <p
                      className="text-sm font-semibold"
                      style={{ color: 'var(--text)' }}
                    >
                      {project.likes.toLocaleString()}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>
                      {project.likes === 1 ? 'person liked' : 'people liked'}{' '}
                      this project
                    </p>
                  </div>
                </div>
              )}
              {project.views > 0 && (
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/20">
                    <FiEye className="w-4 h-4 text-blue-500" />
                  </div>
                  <div>
                    <p
                      className="text-sm font-semibold"
                      style={{ color: 'var(--text)' }}
                    >
                      {project.views.toLocaleString()}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>
                      total views
                    </p>
                  </div>
                </div>
              )}
              {project.clicks > 0 && (
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/20">
                    <FiMousePointer className="w-4 h-4 text-green-500" />
                  </div>
                  <div>
                    <p
                      className="text-sm font-semibold"
                      style={{ color: 'var(--text)' }}
                    >
                      {project.clicks.toLocaleString()}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>
                      total clicks
                    </p>
                  </div>
                </div>
              )}
              {project.shares > 0 && (
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-full bg-purple-100 dark:bg-purple-900/20">
                    <FiShare2 className="w-4 h-4 text-purple-500" />
                  </div>
                  <div>
                    <p
                      className="text-sm font-semibold"
                      style={{ color: 'var(--text)' }}
                    >
                      {project.shares.toLocaleString()}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>
                      shares
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Swiper Custom Styles */}
      <style jsx>{`
        .preview-modal-swiper {
          width: 100%;
          height: 100%;
        }
        .preview-modal-swiper .swiper-button-prev,
        .preview-modal-swiper .swiper-button-next {
          display: none;
        }
        .preview-modal-swiper .swiper-pagination {
          position: absolute;
          bottom: 10px;
        }
        .preview-modal-swiper .swiper-pagination-bullet {
          background: white;
          opacity: 0.5;
        }
        .preview-modal-swiper .swiper-pagination-bullet-active {
          opacity: 1;
        }
        .preview-modal-swiper .swiper-zoom-container {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .preview-modal-swiper .swiper-slide {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          width: 100%;
        }
        .preview-modal-swiper .swiper-wrapper {
          height: 100%;
        }
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.4s ease-out forwards;
        }
      `}</style>
    </div>
  );
}

export default ProjectPreviewModal;
